import { QueryClient } from '@tanstack/react-query';

/**
 * Never retry a rate limit.
 *
 * Retrying a 429 is the client amplifying the outage it is reacting to. The
 * backend limiters are FIXED windows, so the retry — which fires about a second
 * later — lands inside the same exhausted window and cannot succeed. It just
 * spends a second permit that somebody else needed.
 *
 * This matters most on api.vesr, where the `incidents` limiter is 30/min with no
 * partition key: a single bucket shared by every user of the deployment. Under
 * contention `retry: 1` doubled the load exactly when the load was the problem.
 *
 * Everything else keeps the previous single retry.
 */
const shouldRetryQuery = (failureCount: number, error: unknown): boolean => {
	const status = (error as { response?: { status?: number } } | null)?.response?.status;
	if (status === 429) return false;
	return failureCount < 1;
};

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			staleTime: 5 * 60 * 1000, // 5 minutes
			gcTime: 10 * 60 * 1000, // 10 minutes
			retry: shouldRetryQuery,
			refetchOnWindowFocus: false,
		},
	},
});
